/**
 * Precision Variant Picker Module
 * ===============================
 *
 * Compact pill-style variant selector for the Precision Hero PDP.
 *
 * On selection it:
 *  - Updates the visual selected state of the pills.
 *  - Writes the chosen variant id into the add-to-cart form's hidden
 *    input[name="id"] and dispatches a `change` event so product-form picks it up.
 *  - Updates the main price display (current + compare-at).
 *  - Syncs the purchase-options module (one-time / subscription prices) via its
 *    `setVariantData` hook when present.
 *
 * @module components/precision-variant-picker
 *
 * ## HTML Data Attributes
 *
 * Picker container (`[data-precision-variant-picker]`):
 *  - `data-money-format` - Shopify money format
 *
 * Each pill (`[data-variant-pill]`):
 *  - `data-variant-id`        - Variant id
 *  - `data-variant-price`     - Variant unit price in cents
 *  - `data-variant-compare`   - Variant compare-at price in cents (0 when none)
 *  - `data-variant-special`   - Variant special_price_ui metafield in cents (0 when none);
 *                               when 0 < special < price it becomes the selling price and the
 *                               raw price strikes through as compare-at
 *  - `data-variant-available` - 'true' / 'false'
 */

import { formatMoney } from '../utils/format-money.js';

/**
 * Initialize all variant pickers on the page.
 * No-ops when no picker markup is present (feature disabled).
 */
export function initVariantPicker() {
  const pickers = document.querySelectorAll('[data-precision-variant-picker]');
  if (pickers.length === 0) return;

  pickers.forEach((picker) => setupPicker(picker));
}

/**
 * Wire up a single variant picker instance.
 *
 * @param {HTMLElement} picker - The `[data-precision-variant-picker]` element
 */
function setupPicker(picker) {
  const moneyFormat = picker.dataset.moneyFormat || '${{amount}}';
  const pills = Array.from(picker.querySelectorAll('[data-variant-pill]'));
  if (pills.length === 0) return;

  // Scope DOM lookups to the nearest section so multiple heroes don't collide.
  const sectionEl = picker.closest('[data-section-id]') || document;

  /**
   * Apply a variant selection: visual state, form id, prices, purchase-options sync.
   *
   * @param {HTMLElement} selectedPill - The pill chosen by the customer
   */
  function selectVariant(selectedPill) {
    if (!selectedPill || selectedPill.classList.contains('is-unavailable')) return;

    const variantId = selectedPill.dataset.variantId;
    let unitPrice = parseInt(selectedPill.dataset.variantPrice, 10) || 0;
    let comparePrice = parseInt(selectedPill.dataset.variantCompare, 10) || 0;
    // Regular price captured before any special override — subscriptions price off this,
    // since special_price_ui applies to the One-Time option only.
    const regularPrice = unitPrice;
    // Apply the variant-level special price, mirroring precision-product-info.liquid:
    // when 0 < special < price, special becomes the selling price and the raw price strikes through.
    const special = parseInt(selectedPill.dataset.variantSpecial, 10) || 0;
    if (special > 0 && special < unitPrice) {
      comparePrice = unitPrice;
      unitPrice = special;
    }
    // Authoritative per-plan subscription prices for this variant (selling_plan_id → cents),
    // straight from Shopify's selling_plan_allocations. Empty object when none/parse fails.
    let planPrices = {};
    try {
      planPrices = JSON.parse(selectedPill.dataset.variantPlanPrices || '{}');
    } catch (_e) {
      planPrices = {};
    }

    // Visual selected state + radio checked state (immutable per-element updates).
    pills.forEach((pill) => {
      const isSelected = pill === selectedPill;
      pill.classList.toggle('is-selected', isSelected);
      const radio = pill.querySelector('[data-variant-radio]');
      if (radio) radio.checked = isSelected;
    });

    updateFormVariantId(sectionEl, variantId);
    updateMainPrice(sectionEl, unitPrice, comparePrice, moneyFormat);
    syncPurchaseOptions(sectionEl, unitPrice, comparePrice, planPrices, regularPrice);
  }

  pills.forEach((pill) => {
    pill.addEventListener('click', (event) => {
      event.preventDefault();
      selectVariant(pill);
    });

    const radio = pill.querySelector('[data-variant-radio]');
    if (radio) {
      radio.addEventListener('change', () => {
        if (radio.checked) selectVariant(pill);
      });
    }
  });
}

/**
 * Update the add-to-cart form's hidden variant id and notify listeners.
 *
 * @param {ParentNode} scope - Section element (or document) to search within
 * @param {string} variantId - The chosen variant id
 */
function updateFormVariantId(scope, variantId) {
  const input = scope.querySelector('input[name="id"].product-variant-id');
  if (!input || !variantId) return;

  input.value = variantId;
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Update the main price display block to the selected variant.
 *
 * @param {ParentNode} scope - Section element (or document) to search within
 * @param {number} unitPrice - Variant unit price in cents
 * @param {number} comparePrice - Variant compare-at price in cents (0 when none)
 * @param {string} moneyFormat - Shopify money format
 */
function updateMainPrice(scope, unitPrice, comparePrice, moneyFormat) {
  const display = scope.querySelector('[data-precision-price-display]');
  if (!display) return;

  const currentEl = display.querySelector('[data-current-price]');
  const compareEl = display.querySelector('[data-compare-price]');

  if (currentEl) currentEl.textContent = formatMoney(unitPrice, moneyFormat);

  if (compareEl) {
    if (comparePrice > unitPrice) {
      compareEl.textContent = formatMoney(comparePrice, moneyFormat);
      compareEl.classList.remove('tw-hidden');
    } else {
      compareEl.classList.add('tw-hidden');
    }
  }
}

/**
 * Push the selected variant's price into the purchase-options module and reset the
 * purchase selection to the default option (One-Time) — mirroring the old template,
 * which re-renders the section on variant change and lands back on the default.
 * resetPurchaseSelection() runs updateSelection(), which refreshes prices and rebuilds
 * the URL (dropping selling_plan until the customer re-selects a subscription pill).
 * No-ops when no purchase-options module is present.
 *
 * @param {ParentNode} scope - Section element (or document) to search within
 * @param {number} unitPrice - Variant unit price in cents
 * @param {number} comparePrice - Variant compare-at price in cents (0 when none)
 * @param {Object} planPrices - selling_plan_id → subscription price (cents) for this variant
 */
function syncPurchaseOptions(scope, unitPrice, comparePrice, planPrices, regularPrice) {
  const container = scope.querySelector('[data-precision-purchase-options]');
  if (!container || typeof container.setVariantData !== 'function') return;

  // Reprice the options for the new variant first, so the sub pill shows the correct
  // price when the customer reopens it after the reset.
  container.setVariantData(unitPrice, comparePrice, planPrices, regularPrice);

  if (typeof container.resetPurchaseSelection === 'function') {
    container.resetPurchaseSelection();
  } else if (typeof container.updateOptionDisplays === 'function') {
    container.updateOptionDisplays();
  }
}

export default initVariantPicker;
